import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, Renderer2, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PushNotificationService } from 'src/@order/services/push-notification.service';

@Component({
  selector: 'app-push-notification-banner',
  templateUrl: './push-notification-banner.component.html',
  styleUrls: ['./push-notification-banner.component.scss'],
})

export class PushNotificationBannerComponent implements AfterViewInit, OnDestroy {
  private pushNotificationService = inject(PushNotificationService);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  private readonly toastWrapRef = viewChild.required<ElementRef<HTMLDivElement>>('toastWrap');
  private readonly toastStackRef = viewChild.required<ElementRef<HTMLDivElement>>('toastStack');
  private readonly countPillRef = viewChild.required<ElementRef<HTMLDivElement>>('countPill');

  private stackListeners: Array<() => void> = [];
  private toasts: Array<{ id: number; el: HTMLDivElement; actionUrl?: string; autoCloseTimer?: any; autoCloseMs?: number; autoCloseRemaining?: number; autoCloseStartedAt?: number }> = [];
  private expanded = false;
  private hovering = false;

  private readonly GAP = 6;
  private readonly SCALE_STEP = 0.04;
  private readonly REMOVE_ANIMATION_MS = 400;
  public toastMarginTopPx = 10;

  ngAfterViewInit(): void {
    const stack = this.toastStackRef().nativeElement;
    this.updateTopFromHeader();

    this.stackListeners.push(
      this.renderer.listen(window, 'resize', () => this.updateTopFromHeader()),
      this.renderer.listen(stack, 'mouseenter', () => {
        this.hovering = true;
        this.syncAutoCloseTimers();
        if (this.toasts.length > 1) {
          this.expanded = true;
          this.layout();
        }
      }),
      this.renderer.listen(stack, 'mouseleave', () => {
        this.hovering = false;
        this.expanded = false;
        this.syncAutoCloseTimers();
        this.layout();
      }),
    );

    this.pushNotificationService.pushBanner$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((banner) => {
        if (!banner) {
          return;
        }
        this.showInfo(banner.title, banner.body, banner.actionUrl, banner.autoCloseAfter);
      });
  }

  ngOnDestroy(): void {
    this.stackListeners.forEach((cleanup) => cleanup());
    this.toasts.forEach((t) => {
      if (t.autoCloseTimer) {
        clearTimeout(t.autoCloseTimer);
      }
    });
  }

  private showInfo(title: string, desc = '', actionUrl?: string, autoCloseAfter?: string | number): void {
    console.log('[push-banner] showInfo', { title, desc, actionUrl, autoCloseAfter, type: typeof autoCloseAfter });
    const id = Date.now();
    const el = this.renderer.createElement('div') as HTMLDivElement;

    el.className = [
      'absolute left-0 right-0 flex items-start gap-2.5',
      'rounded-xl border border-blue-theme-color bg-[#FF4D23] text-white-color shadow-lg px-4 py-3 text-sm box-border',
      'transition-[transform,opacity,top] duration-[380ms] ease-[cubic-bezier(.22,1,.36,1)]',
      '-translate-y-full opacity-0',
    ].join(' ');

    el.innerHTML = `
      ${this.infoIcon}
      <div class="flex-1 min-w-0">
        <p class="font-medium text-white-color leading-snug">${title}</p>
        ${desc ? `<p class="text-xs text-white-color mt-0.5 leading-snug">${desc}</p>` : ''}
      </div>
      <button data-close class="shrink-0 text-white-color opacity-80 hover:opacity-100 hover:bg-white/20 rounded p-0.5 text-base leading-none transition-colors">✕</button>
    `;

    const closeBtn = el.querySelector('[data-close]') as HTMLButtonElement | null;
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.dismiss(id);
    });

    el.addEventListener('click', () => {
      this.openNotification(actionUrl);
      this.dismiss(id);
    });

    this.toastStackRef().nativeElement.appendChild(el);
    const toast: { id: number; el: HTMLDivElement; actionUrl?: string; autoCloseTimer?: any; autoCloseMs?: number; autoCloseRemaining?: number; autoCloseStartedAt?: number } = { id, el, actionUrl };
    this.toasts.push(toast);

    const timeoutMs = this.resolveAutoCloseMs(autoCloseAfter);
    if (timeoutMs > 0) {
      toast.autoCloseMs = timeoutMs;
      toast.autoCloseRemaining = timeoutMs;
    }

    this.syncAutoCloseTimers();
    this.layout();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        el.classList.remove('-translate-y-full', 'opacity-0');
        el.classList.add('translate-y-0', 'opacity-100');
        this.layout();
      }),
    );
  }

  private dismiss(id: number): void {
    const idx = this.toasts.findIndex((t) => t.id === id);
    if (idx === -1) {
      return;
    }

    const { el, autoCloseTimer } = this.toasts[idx];
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
    }
    el.classList.add('-translate-y-full', 'opacity-0');
    this.toasts.splice(idx, 1);
    if (this.toasts.length <= 1) {
      this.expanded = false;
    }
    this.syncAutoCloseTimers();
    this.layout();
    setTimeout(() => el.remove(), this.REMOVE_ANIMATION_MS);
  }

  private resolveAutoCloseMs(autoCloseAfter?: string | number): number {
    if (autoCloseAfter === undefined || autoCloseAfter === null || autoCloseAfter === '') {
      return 0;
    }
    const parsed = Number(autoCloseAfter);
    if (isNaN(parsed) || parsed <= 0) {
      return 0;
    }
    return parsed;
  }

  private syncAutoCloseTimers(): void {
    const shouldRun = !this.hovering;
    const now = Date.now();
    console.log('[push-banner] syncAutoCloseTimers', { shouldRun, hovering: this.hovering, toastCount: this.toasts.length });
    this.toasts.forEach((t) => {
      const hasDuration = t.autoCloseRemaining !== undefined && t.autoCloseRemaining > 0;
      if (shouldRun && hasDuration && !t.autoCloseTimer) {
        t.autoCloseStartedAt = now;
        t.autoCloseTimer = setTimeout(() => {
          console.log('[push-banner] auto-close timer fired for toast', t.id);
          this.dismiss(t.id);
        }, t.autoCloseRemaining!);
        console.log('[push-banner] scheduled auto-close', { id: t.id, ms: t.autoCloseRemaining });
      } else if (!shouldRun && t.autoCloseTimer) {
        clearTimeout(t.autoCloseTimer);
        t.autoCloseTimer = undefined;
        if (t.autoCloseStartedAt !== undefined && t.autoCloseRemaining !== undefined) {
          const elapsed = now - t.autoCloseStartedAt;
          t.autoCloseRemaining = Math.max(0, t.autoCloseRemaining - elapsed);
        }
      }
    });
  }

  private layout(): void {
    const stack = this.toastStackRef().nativeElement;
    const pill = this.countPillRef().nativeElement;
    const n = this.toasts.length;

    if (n === 0) {
      stack.style.height = '0px';
      this.updatePill(0, false, pill);
      return;
    }

    if (this.expanded) {
      let top = 0;
      [...this.toasts].reverse().forEach((t, i) => {
        Object.assign(t.el.style, {
          top: `${top}px`,
          transform: 'scale(1)',
          opacity: '1',
          zIndex: `${i + 1}`,
        });
        const close = t.el.querySelector('[data-close]') as HTMLElement | null;
        if (close) {
          close.style.cssText = 'opacity:1;pointer-events:all';
        }
        top += t.el.offsetHeight + this.GAP;
      });
      stack.style.height = `${top}px`;
      this.updatePill(n, true, pill);
      return;
    }

    const topH = this.toasts[n - 1].el.offsetHeight;
    const visible = Math.min(n, 3);

    [...this.toasts].reverse().forEach((t, i) => {
      const isTop = i === 0;
      Object.assign(t.el.style, {
        top: `${i * this.GAP}px`,
        transform: `scale(${1 - i * this.SCALE_STEP})`,
        opacity: i < 3 ? `${1 - i * 0.18}` : '0',
        zIndex: `${n - i}`,
      });
      const close = t.el.querySelector('[data-close]') as HTMLElement | null;
      if (close) {
        close.style.cssText = isTop
          ? 'opacity:1;pointer-events:all'
          : 'opacity:0;pointer-events:none';
      }
    });

    stack.style.height = `${topH + Math.min(visible - 1, 2) * this.GAP}px`;
    this.updatePill(n, false, pill);
  }

  private updatePill(count: number, isExpanded: boolean, pill: HTMLDivElement): void {
    const show = count > 1 && !isExpanded;
    pill.textContent = `${count} notifications`;
    pill.classList.toggle('opacity-0', !show);
    pill.classList.toggle('scale-75', !show);
    pill.classList.toggle('opacity-100', show);
    pill.classList.toggle('scale-100', show);
  }

  private openNotification(actionUrl?: string): void {
    if (!actionUrl) {
      return;
    }
    const normalizedAction = actionUrl.startsWith('/')
      ? actionUrl
      : `/${actionUrl}`;

    if (/^https?:\/\//i.test(actionUrl)) {
      try {
        const parsedUrl = new URL(actionUrl);
        if (parsedUrl.origin === window.location.origin) {
          const inAppPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
          this.router.navigateByUrl(inAppPath).catch(() => {
            window.location.href = actionUrl;
          });
        } else {
          window.location.href = actionUrl;
        }
      } catch (_) {
        window.location.href = actionUrl;
      }
      return;
    }

    this.router.navigateByUrl(normalizedAction).catch(() => {
      window.location.href = normalizedAction;
    });
  }

  private updateTopFromHeader(): void {
    const header = document.querySelector('app-header') as HTMLElement | null;
    const headerHeight = header?.offsetHeight || 0;
    this.toastMarginTopPx = headerHeight > 0 ? headerHeight + 8 : 10;
  }

  private readonly infoIcon = `<svg class="shrink-0 mt-0.5" width="15" height="15" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="#FFFFFF" stroke-width="1.5"/>
    <path d="M8 7v4" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="8" cy="5" r="0.75" fill="#FFFFFF"/>
  </svg>`;
}
